<template>
  <div class="sam-checkbox__wrapper">
    <label
      :for="checkboxId"
      :class="[
        'sam-checkbox',
        `is-${samType}`,
        { 'is-disabled': disabled },
        { 'is-rounded': rounded },
      ]"
      data-samora
    >
      <input
        :id="checkboxId"
        ref="checkboxElem"
        v-model="value"
        :disabled="disabled"
        class="sam-checkbox__input"
        type="checkbox"
        @change="selectOption"
      />

      <span
        :class="[
          'sam-checkbox__label',
          { 'with-content': !!label },
          { 'is-compact': compact },
          { 'with-caption': caption },
        ]"
      >
        <span
          v-if="!$slots.label"
          class="sam-checkbox__label-text"
        >
          {{ label }}
        </span>
        <span
          v-else
          class="sam-checkbox__label-text"
        >
          <slot name="label" />
        </span>
      </span>
    </label>

    <span
      v-if="caption && label"
      class="sam-checkbox__caption"
    >
      {{ caption }}
    </span>
  </div>
</template>

<script>
import SAMORA_TYPES from '../../../utils/constants/samora-types.enum';
import { randomizeId } from '../../../utils/utils';

const { CHECKBOX: SAMORA_CHECKBOX_TYPES } = SAMORA_TYPES;

export const CHECKBOX_TYPES = SAMORA_CHECKBOX_TYPES;

export default {
  name: 'SamCheckbox',
  model: {
    prop: 'data',
    event: 'change',
  },
  props: {
    /**
     * The Checkbox label
     */
    label: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * The Checkbox data variable
     */
    data: {
      type: [Boolean, String, Number],
      required: false,
      default: false,
    },
    /**
     * The Checkbox caption
     */
    caption: {
      type: String,
      required: false,
      default: null,
    },
    /**
     * Checkbox icon type
     * @values default, indeterminate
     */
    samType: {
      type: String,
      required: false,
      default: 'default',
      validator: (val) => Object.values(CHECKBOX_TYPES).includes(val),
    },
    /**
     * Override the Checkbox default boolean data model (you can set and get the component value according to this model)
     * @values [value for checked, value for unchecked]
     */
    dataModel: {
      type: Array,
      required: false,
      default: () => [true, false],
    },
    /**
     * If the Button is disabled (html attribute disabled)
     * @values true, false
     */
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    /** If the Checkbox should be compact (i.e., no outer padding)
     * @values true, false
     */
    compact: {
      type: Boolean,
      required: false,
      default: false,
    },
    /** If the Checkbox should be rounded
     * @values true, false
     */
    rounded: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      checkboxId: randomizeId('checkboxid'),
      value: false,
    };
  },
  watch: {
    data: {
      handler(newVal) {
        const [checked, unchecked] = this.dataModel;

        if (newVal === checked) {
          this.value = true;
        } else if (newVal === unchecked) {
          this.value = false;
        } else {
          // eslint-disable-next-line no-console
          console.error(
            `Invalid data value for Checkbox - Does not correlate with the Checkbox dataModel - ${this.dataModel}`
          );
        }
      },
      immediate: true,
    },
  },
  methods: {
    selectOption() {
      /**
       * Triggers when Checkbox changes it's value
       * @event change
       * @property {boolean, string} value new value set
       */
      const [checked, unchecked] = this.dataModel;

      if (this.value) {
        this.$emit('change', checked);
      } else {
        this.$emit('change', unchecked);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.sam-checkbox {
  @extend .m-0, .p-0, .d-inline-flex;
  border: none;
  background-color: transparent;

  &__wrapper {
    @extend .d-flex, .flex-column;
  }

  &__caption {
    @extend .text--caption, .d-block;
    color: $neutral--80;
    overflow-wrap: break-word;
    white-space: normal;
    padding-left: calc(
      $size--5 + $size--0--fourth + $size--2
    ); // width, border and margin of checkbox
  }

  &::-moz-focus-inner {
    border: 0;
  }

  &.is-disabled {
    .sam-checkbox__label {
      cursor: not-allowed;
      color: $neutral--50 !important;

      &::before {
        border-color: $neutral--30 !important;
        box-shadow: none !important;
        background-color: $neutral--10 !important;
      }
    }

    .sam-checkbox__input:checked + .sam-checkbox__label::before {
      background-color: $neutral--50 !important;
    }
  }

  &__input {
    width: $size--0;
    height: $size--0;
    visibility: hidden;
    opacity: 0;
    position: absolute;

    &:checked + .sam-checkbox__label {
      @include mix-weight--medium;
    }
  }

  &.is-default {
    .sam-checkbox__input:checked + .sam-checkbox__label::before {
      background-image: url("data:image/svg+xml,%3C%3Fxml version='1.0' encoding='UTF-8'%3F%3E%3Csvg width='20px' height='20px' viewBox='0 0 20 20' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3C!-- Generator: Sketch 61.2 (89653) - https://sketch.com --%3E%3Ctitle%3ECheckbox/selected%3C/title%3E%3Cdesc%3ECreated with Sketch.%3C/desc%3E%3Cg id='Checkbox/selected' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd' opacity='1'%3E%3Cpath d='M14.6986428,6.27716754 C14.7038152,6.28222495 14.7089305,6.28734032 14.713988,6.29251267 L14.7209017,6.29958353 C15.1008922,6.6882107 15.1008922,7.30919077 14.7209017,7.69781794 L8.84478758,13.7074873 C8.47783666,14.0827785 7.87985994,14.0953942 7.49749137,13.7403157 C7.45564786,13.710907 7.41534638,13.6775103 7.37733117,13.6403399 L7.36198604,13.6249948 L7.36198604,13.6249948 L5.27909832,11.4947661 C4.89910778,11.106139 4.89910778,10.4851589 5.27909832,10.0965317 L5.28601205,10.0894609 C5.65888656,9.70811148 6.27030585,9.70124123 6.65165524,10.0741157 C6.6568276,10.0791732 6.66194296,10.0842885 6.66700037,10.0894609 L8.143,11.599 L13.3329996,6.29251267 C13.7058741,5.91116328 14.3172934,5.90429303 14.6986428,6.27716754 Z' id='Combined-Shape' fill='%23FFFFFF'%3E%3C/path%3E%3C/g%3E%3C/svg%3E");
      background-color: $informative--main;
    }
  }

  &.is-indeterminate {
    .sam-checkbox__input:checked + .sam-checkbox__label::before {
      background-color: $informative--main;
      background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='20' height='20' rx='4' fill='%233843D0'/%3E%3Crect x='5' y='9' width='10' height='2' rx='1' fill='%23fff'/%3E%3C/svg%3E%0A");
    }
  }

  &.is-rounded {
    .sam-checkbox__label::before {
      border-radius: 50%;
    }
    .sam-checkbox__input:checked + .sam-checkbox__label::before {
      background-color: $pure--white;
      background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg width='20' height='20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='10' cy='10' r='5' fill='%233843D0'/%3E%3C/svg%3E");
    }
  }

  &__label {
    @extend .py-2, .px-0, .d-flex, .flex-center, .text--body-2;

    color: $pure--black;
    text-align: left;
    width: 100%;
    height: 100%;
    cursor: pointer;
    width: 100%;
    height: 100%;
    outline: none;

    &::-moz-focus-inner {
      border: 0;
    }

    &::before {
      content: '';
      position: relative;
      width: $size--5;
      height: $size--5;
      border: $size--0--fourth solid $neutral--70;
      border-radius: $size--1;
      background-color: $pure--white;
      display: block;
      transition: all $default-time $default-easing;
    }

    &.with-content::before {
      margin-right: $size--2;
    }

    &.is-compact {
      @extend .py-0;
    }

    &.with-caption {
      @extend .pb-0;
    }
  }
}
</style>
